<script setup>
import misc404 from '@/assets/images/pages/404.png'
import miscMaskDark from '@/assets/images/pages/misc-mask-dark.png'
import miscMaskLight from '@/assets/images/pages/misc-mask-light.png'
import topImage from '@/assets/images/pages/fineDining1.webp'


import logo from '@/assets/logo-400.png'
import logoLargeLight from '@/assets/logo-large-light.svg'
import { useGenerateImageVariant } from '@core/composable/useGenerateImageVariant'
import axios from "@axios"
import { isUserLoggedIn } from '@/router/utils'

import {
  alphaDashValidator,
  emailValidator,
  requiredValidator
} from '@validators'
import {useRoute} from "vue-router"
import { safeRedirectPath } from '@/utils/safeRedirect'

const authThemeMask = useGenerateImageVariant(miscMaskLight, miscMaskDark)

// RM-223: only funnel a logged-OUT guest back to the group join page; a
// logged-in member is no longer a guest, so clear the stale value instead of
// trapping them in a redirect loop back to /{group}/activate. See home/[...all].vue.
let guestUserGroup = JSON.parse(localStorage.getItem('guestUserGroup'))
if(guestUserGroup && !isUserLoggedIn()){
  window.location.href = `${window.location.origin}${guestUserGroup.joinUrl}`
} else if (guestUserGroup) {
  localStorage.removeItem('guestUserGroup')
}

const route = useRoute()
const router = useRouter()
const lastPathSegment = route.params.all
let userPreviouslyAuthenticated = (localStorage.getItem('userLastAuthenticatedAt') ? true : false)
const refVForm = ref(null)
const formMode = ref(lastPathSegment || (userPreviouslyAuthenticated ? 'login' : 'join'))
const showForm = ref((lastPathSegment === 'join' || lastPathSegment === 'login' || userPreviouslyAuthenticated))
const showFormComplete = ref(false)
const email = ref(route?.query?.email || '')
const password = ref('')
const termsAccepted = ref(true)
const isPasswordVisible = ref(false)

// Form Errors
const errors = ref({
  email: undefined,
  password: undefined,
  termsAccepted: undefined
})

const clearErrors = () => {
  if (errors.value) {
    errors.value = {}
  }
}

const onSubmitJoin = () => {
  clearErrors()

  refVForm.value?.validate().then(({valid: isValid}) => {
    if (isValid)
      register()
  })
}


const onSubmitLogin = () => {
  clearErrors()

  refVForm.value?.validate().then(({valid: isValid}) => {
    if (isValid)
      login()
  })
}

const register = () => {
  axios.post('/auth/register/', {
    email: email.value,
    password: password.value,
    termsAccepted: termsAccepted.value,
  }).then(r => {
    const {userData, userAbilities, jwt} = r.data
    localStorage.setItem('userAbilities', JSON.stringify(userAbilities))

    // ability.update(userAbilities)
    localStorage.setItem('userData', JSON.stringify(userData))
    localStorage.setItem('accessToken', JSON.stringify(jwt))
    localStorage.setItem('jwt', JSON.stringify(jwt))
    // C9: validate ?to= against an allowlist before the post-login reload.
    location.href = safeRedirectPath(route.query.to) || '/m/'

    return null
  }).catch(e => {
    console.error(e)
    const {errors: formErrors} = e.response.data
    errors.value = formErrors
  })
}

const login = () => {
  axios.post('/auth/login', {
    email: email.value,
    password: password.value
  }).then(r => {
    const {userData, userAbilities, jwt} = r.data
    localStorage.setItem('userAbilities', JSON.stringify(userAbilities))

    //ability.update(userAbilities)
    localStorage.setItem('userData', JSON.stringify(userData))
    localStorage.setItem('accessToken', JSON.stringify(jwt))
    localStorage.setItem('jwt', JSON.stringify(jwt))
    // C9: validate ?to= against an allowlist before the post-login reload.
    const safeTo = safeRedirectPath(route.query.to)
    if (safeTo)
      location.href = safeTo
    else if (userData.userLevel == "admin")
      location.href='/reservations-review'
    else
      location.href='/m/'

    return null
  }).catch(e => {
    console.error(e)
    if(e.response){
      const {errors: formErrors} = e.response.data
      errors.value = formErrors
    }
  })
}
</script>

<template>
  <div>
    <section class="home-masthead">
      <div class="home-masthead-bkg-holder">
        <img
          class="home-masthead-bkg"
          :src="topImage"
        >
      </div>
      <div class="content-wrapper">
        <div class="masthead-content ">
          <div class="text-5xl line">
            <b>Hassle-Free Booking. VIP Everywhere.</b>
          </div>
          <div class="ma-4">
            Get peak times at favorite spots without the need for favors or waitlists, and benefit from easy group bookings with one inquiry to multiple venues.
          </div>
          <VForm
            ref="refVForm"
            validate-on="blur"
          >
            <VTextField
              v-model="email"
              autocomplete="email"
              :rules="[requiredValidator, emailValidator]"
              :error-messages="errors.email"
              label="email"
              type="email"
              class="mb-2"
            />
            <VTextField
              v-model="password"
              autocomplete="new-password"
              :rules="[requiredValidator]"
              :error-messages="errors.password"
              label="password"
              :type="isPasswordVisible ? 'text' : 'password'"
              :append-inner-icon="isPasswordVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
              @click:append-inner="isPasswordVisible = !isPasswordVisible"
            />
            <div
              v-if="formMode == 'login'"
              class="d-flex align-center flex-wrap justify-space-between mt-2 mb-6"
            >
              <!--                    <VCheckbox -->
              <!--                      v-model="rememberMe" -->
              <!--                      label="Remember me" -->
              <!--                    /> -->
              <VSpacer />
              <RouterLink
                class="text-primary ms-2 mb-1"
                :to="{ name: 'forgot-password' }"
              >
                reset password?
              </RouterLink>
            </div>
            <VExpandTransition>
              <div
                v-if="formMode == 'join'"
                class="d-flex align-center mt-3"
              >
                <VCheckbox
                  id="privacy-policy"
                  v-model="termsAccepted"
                  inline
                  :rules="[requiredValidator]"
                  :error-messages="errors.termsAccepted"
                />
                <VLabel
                  for="privacy-policy"
                  style="opacity: 1;"
                >
                  <span class="me-1">I agree to the </span>
                  <RouterLink
                    to="/terms"
                    class="text-primary"
                  >
                    terms of service
                  </RouterLink>
                </VLabel>
              </div>
            </VExpandTransition>
            <VBtn
              v-if="formMode == 'join'"
              class="mt-6"
              block
              type="submit"
              @click.prevent="onSubmitJoin"
            >
              join
            </VBtn>
            <VBtn
              v-else
              class="mt-6"
              block
              type="submit"
              @click.prevent="onSubmitLogin"
            >
              log in
            </VBtn>
          </VForm>

          <p
            v-if="formMode == 'join'"
            class="mt-4"
          >
            already a member?
            <a
              class="text-primary ms-2"
              @click="formMode = 'login'; showFormComplete = false; showForm = true; clearErrors();"
            >Log in
            </a>
          </p>

          <p
            v-else
            class="mt-4"
          >
            new to RM?
            <a
              class="text-primary ms-2"
              @click="formMode = 'activate'; showFormComplete = false; showForm = true; clearErrors();"
            >Request Access
            </a>
          </p>
          <!--            <p class="mb-0"><b>membership tiers</b> -->
          <!--              <br>Premium: $100/3 months, $250/yr</p> -->
          <!--            <p class="ma-0">Platinum: $500/3 months, $1,500/yr</p> -->
          <!--            <p class="ma-0">Standard: Free</p> -->
          <!--            <p class="mt-3">view <RouterLink -->
          <!--              class="text-primary" -->
          <!--              :to="{ name: 'membership' }" -->
          <!--            >membership tiers</RouterLink> -->
          <!---->
          <!--            </p> -->
        </div>
      </div>
    </section>
    <!--      <div class="subtle-restaurant-logos"> -->
    <!--        <div class="subtle-restaurant-logos-wrapper"> -->
    <!--          <div class="subtle-restaurant-logo-holder"> -->
    <!--            <img class="subtle-restaurant-logo subtle-restaurant-logo&#45;&#45;small" src="@/assets/images/restaurants/logos/etta_logo.png"/> -->
    <!--          </div> -->
    <!--          <div class="subtle-restaurant-logo-holder"> -->

    <!--            <img class="subtle-restaurant-logo subtle-restaurant-logo&#45;&#45;small" src="@/assets/images/restaurants/logos/sushi_san_logo.png"/> -->
    <!--          </div> -->
    <!--          <div class="subtle-restaurant-logo-holder"> -->
    <!--            <img class="subtle-restaurant-logo subtle-restaurant-logo&#45;&#45;smallest" src="@/assets/images/restaurants/logos/aba_logo.png"/> -->
    <!--          </div> -->

    <!--          <div class="subtle-restaurant-logo-holder"> -->
    <!--            <img class="subtle-restaurant-logo" src="@/assets/images/restaurants/logos/bub_city_logo.png"/> -->
    <!--          </div> -->
    <!--          <div class="subtle-restaurant-logo-holder"> -->
    <!--            <img class="subtle-restaurant-logo subtle-restaurant-logo&#45;&#45;smallest" src="@/assets/images/restaurants/logos/hub_51_logo.png"/> -->
    <!--          </div> -->
    <!--          <div class="subtle-restaurant-logo-holder"> -->
    <!--            <img class="subtle-restaurant-logo subtle-restaurant-logo&#45;&#45;medium" src="@/assets/images/restaurants/logos/pizzeria_portofino_logo.png"/> -->
    <!--          </div> -->
    <!--          <div class="subtle-restaurant-logo-holder"> -->
    <!--            <img class="subtle-restaurant-logo subtle-restaurant-logo&#45;&#45;smaller" src="@/assets/images/restaurants/logos/ema_logo.png"/> -->
    <!--          </div> -->
    <!--        </div> -->
    <!--      </div> -->

    <!--      <section class="pb-2"> -->
    <!--        <div class="content-wrapper"> -->
    <!--          <div class="testimonials-holder"> -->
    <!--            <div class="testimonials-wrapper"> -->
    <!--              <a class="twitter-timeline" href="https://twitter.com/RM_Preferred?ref_src=twsrc%5Etfw">Tweets by RM_Preferred</a> <component :is="'script'" async src="https://platform.twitter.com/widgets.js" charset="utf-8"></component> -->
    <!--            </div> -->
    <!--          </div> -->

    <!--        </div> -->
    <!--      </section> -->


    <HomeOverview />

    <section class="pb-10">
      <div class="content-wrapper">
        <h2 class="section-subtitle text-center mt-0">
          membership tiers
        </h2>
        <div class="section-subtitle text-center mt-4">
          <b>Platinum</b><br>$400pp/yr - group rates for 5+<br>
          (subject to review & availability)
        </div>
        <div class="section-subtitle text-center mt-4">
          <b>Premium</b><br>$200pp/yr
        </div>
        <div class="section-subtitle text-center mt-4">
          <b>Standard</b><br>Free
        </div>
        <div class="section-subtitle text-center mt-2">
          <RouterLink
            class="text-primary"
            :to="{ name: 'membership' }"
          >
            view more on benefits & plans here
          </RouterLink>
        </div>
      </div>
    </section>

    <HomeTestimonials />


    <!--      <v-sheet class="py-16"> -->
    <!--        <section id="filter"> -->
    <!--          <v-container> -->
    <!--            <v-row justify="space-between"> -->
    <!--              <v-col cols="auto"> -->
    <!--                <v-responsive width="350"> -->
    <!--                  <h2 class="text-h4"> -->
    <!--                    Become a Member -->
    <!--                  </h2> -->

    <!--                  <p class="text-success mt-3"> -->
    <!--                    Exclusive Access -->
    <!--                  </p> -->

    <!-- &lt;!&ndash;                  <strong class="mt-3">&ndash;&gt; -->
    <!-- &lt;!&ndash;                    Version 3 Only&ndash;&gt; -->
    <!-- &lt;!&ndash;                  </strong>&ndash;&gt; -->

    <!--                  <p class="mt-8"> -->
    <!--                    For an annual fee members gain access to prime time reservations and private dining at their favorite restaurants. -->
    <!--                  </p> -->

    <!--                  <p class="mt-8"> -->
    <!--                    Our members get access to tables not available to the general public. -->
    <!--                  </p> -->

    <!--                  <v-btn -->
    <!--                    class="mt-6" -->
    <!--                    to="/members" -->
    <!--                  > -->
    <!--                    Learn More -->
    <!--                  </v-btn> -->
    <!--                  <v-btn -->
    <!--                    class="mt-6 ml-6" -->
    <!--                    to="/register" -->
    <!--                  > -->
    <!--                    JOIN NOW -->
    <!--                  </v-btn> -->
    <!--                </v-responsive> -->
    <!--              </v-col> -->

    <!--              <v-img -->
    <!--                max-width="400" -->
    <!--                :src="reservedImage" -->
    <!--              /> -->
    <!--            </v-row> -->
    <!--          </v-container> -->
    <!--        </section> -->

    <!--        <v-container> -->
    <!--          <v-divider class="my-12" /> -->
    <!--        </v-container> -->

    <!--        <section id="controls"> -->
    <!--          <v-container> -->
    <!--            <v-row justify="space-between"> -->
    <!--              <v-col cols="auto"> -->
    <!--                <v-responsive width="350"> -->
    <!--                  <h2 class="text-h4"> -->
    <!--                    Most Booked in Chicago -->
    <!--                  </h2> -->

    <!--                  <p class="text-success mt-3"> -->
    <!--                    And the list goes on... -->
    <!--                  </p> -->

    <!--                  <p class="mt-3"> -->
    <!--                    Aba, Adalina, Alla Vita, Avec, Bambola, Bazaar Meat, Chicago Cut, Duck Duck Goat, etta, Gibsons Italia, Gino & Marty's, Girl & the Goat, Le Colonial, Lyra, Maple & Ash, Momotaro, Proxi, RL Restaurant, RPMs, Steak 48, Summer House, Sunda, Sushi-San -->
    <!--                  </p> -->


    <!--                  <v-btn -->
    <!--                    class="mt-6" -->
    <!--                    to="/restaurants" -->
    <!--                  > -->
    <!--                    See All Restaurants -->
    <!--                  </v-btn> -->
    <!--                </v-responsive> -->
    <!--              </v-col> -->

    <!--              <v-img -->
    <!--                max-width="400" -->
    <!--                :src="chicagoImage" -->
    <!--              /> -->
    <!--            </v-row> -->
    <!--          </v-container> -->
    <!--        </section> -->

    <!--        <v-container> -->
    <!--          <v-divider class="my-12" /> -->
    <!--        </v-container> -->

    <!--        <section id="grid"> -->
    <!--          <v-container> -->
    <!--            <v-row justify="space-between"> -->
    <!--              <v-col cols="auto"> -->
    <!--                <v-responsive width="350"> -->
    <!--                  <h2 class="text-h4"> -->
    <!--                    Most Booked in New York -->
    <!--                  </h2> -->

    <!--                  <p class="text-success mt-3"> -->
    <!--                    And the list goes on... -->
    <!--                  </p> -->

    <!--                  <p class="mt-3"> -->
    <!--                    Aba, Adalina, Alla Vita, Avec, Bambola, Bazaar Meat, Chicago Cut, Duck Duck Goat, etta, Gibsons Italia, Gino & Marty's, Girl & the Goat, Le Colonial, Lyra, Maple & Ash, Momotaro, Proxi, RL Restaurant, RPMs, Steak 48, Summer House, Sunda, Sushi-San -->
    <!--                  </p> -->


    <!--                  <v-btn -->
    <!--                    class="mt-6" -->
    <!--                    to="/restaurants" -->
    <!--                  > -->
    <!--                    See All Restaurants -->
    <!--                  </v-btn> -->
    <!--                </v-responsive> -->
    <!--              </v-col> -->

    <!--              <v-img -->
    <!--                max-width="400" -->
    <!--                :src="nycImage" -->
    <!--              /> -->
    <!--            </v-row> -->
    <!--          </v-container> -->
    <!--        </section> -->

    <!--        <v-container> -->
    <!--          <v-divider class="my-12" /> -->
    <!--        </v-container> -->

    <!--        <section id="grid"> -->
    <!--          <v-container> -->
    <!--            <v-row justify="space-between"> -->
    <!--              <v-col cols="auto"> -->
    <!--                <v-responsive width="350"> -->
    <!--                  <h2 class="text-h4"> -->
    <!--                    Most Booked in Miami -->
    <!--                  </h2> -->

    <!--                  <p class="text-success mt-3"> -->
    <!--                    And the list goes on... -->
    <!--                  </p> -->

    <!--                  <p class="mt-3"> -->
    <!--                    Aba, Adalina, Alla Vita, Avec, Bambola, Bazaar Meat, Chicago Cut, Duck Duck Goat, etta, Gibsons Italia, Gino & Marty's, Girl & the Goat, Le Colonial, Lyra, Maple & Ash, Momotaro, Proxi, RL Restaurant, RPMs, Steak 48, Summer House, Sunda, Sushi-San -->
    <!--                  </p> -->


    <!--                  <v-btn -->
    <!--                    class="mt-6" -->
    <!--                    to="/restaurants" -->
    <!--                  > -->
    <!--                    See All Restaurants -->
    <!--                  </v-btn> -->
    <!--                </v-responsive> -->
    <!--              </v-col> -->

    <!--              <v-img -->
    <!--                max-width="400" -->
    <!--                :src="miamiImage" -->
    <!--              /> -->
    <!--            </v-row> -->
    <!--          </v-container> -->
    <!--        </section> -->
    <!--      </v-sheet> -->

    <!--      <v-sheet -->
    <!--        class="py-16" -->
    <!--        color="#787878" -->
    <!--      > -->
    <!--        <section id="grid"> -->
    <!--          <v-container> -->
    <!--            <v-row justify="space-between"> -->
    <!--              <v-col cols="auto"> -->
    <!--                <v-responsive -->
    <!--                  class="overflow-visible" -->
    <!--                  width="350" -->
    <!--                > -->
    <!--                  <h2 class="text-h4"> -->
    <!--                    RM for Restaurants -->
    <!--                  </h2> -->

    <!--                  <p class="text-success mt-3"> -->
    <!--                    Receive high quality referrals at ZERO cost. -->
    <!--                  </p> -->

    <!-- &lt;!&ndash;                  <strong>Become a sponsor</strong>&ndash;&gt; -->

    <!--                  <p class="mt-8"> -->
    <!--                    Our members want to spend good money at your restaurant and are willing to guarantee it.                  </p> -->

    <!--                  <v-btn -->
    <!--                    class="mt-6 " -->
    <!--                    to="/preferred" -->
    <!--                  > -->
    <!--                    Learn More -->
    <!--                  </v-btn> -->
    <!--                </v-responsive> -->
    <!--              </v-col> -->
    <!--            </v-row> -->
    <!--          </v-container> -->
    <!--        </section> -->
    <!--      </v-sheet> -->
  </div>
</template>

<style lang="scss" scoped>
@use "@core/scss/pages/misc.scss";

a {
  cursor: pointer;
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: Auth
</route>
